<template>
  <article class="comment" :class="{ 'comment--reply': depth > 0 }">
    <div class="comment__rail" aria-hidden="true"></div>
    <div class="comment__content">
      <header class="comment__header">
        <img
          v-if="comment.authorPhoto"
          :src="comment.authorPhoto"
          :alt="`${comment.authorName} profile`"
          class="comment__avatar"
          referrerpolicy="no-referrer"
        >
        <span v-else class="comment__avatar comment__avatar--fallback" aria-hidden="true">
          {{ initial }}
        </span>
        <div>
          <strong>{{ comment.authorName }}</strong>
          <span v-if="formattedDate || comment.updatedAt" class="comment__meta">
            <time v-if="formattedDate" :datetime="isoDate">{{ formattedDate }}</time>
            <span v-if="comment.updatedAt">Edited</span>
          </span>
        </div>
      </header>
      <form v-if="editing" class="comment__edit" @submit.prevent="saveEdit">
        <textarea
          :id="editFieldId"
          ref="editField"
          v-model="editDraft"
          :aria-label="`Edit ${commentLabel}`"
          maxlength="1200"
          rows="4"
        ></textarea>
        <div class="comment__edit-actions">
          <small>{{ editDraft.length }}/1200</small>
          <div>
            <button type="button" :disabled="saving" @click="cancelEdit">Cancel</button>
            <button
              type="submit"
              class="comment__save"
              :disabled="!canSaveEdit || saving"
            >
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </form>
      <p v-else>{{ comment.body }}</p>

      <div v-if="!editing" class="comment__actions">
        <button type="button" @click="$emit('reply', comment)">Reply</button>
        <button v-if="canEdit" type="button" @click="beginEdit">Edit</button>
        <button
          v-if="canDelete"
          type="button"
          class="comment__delete"
          @click="confirmingDelete = true"
        >
          Delete
        </button>
      </div>

      <div v-if="confirmingDelete" class="comment__delete-confirmation">
        <span>Delete this {{ commentLabel }}? Replies to it will remain.</span>
        <div>
          <button type="button" :disabled="deleting" @click="confirmingDelete = false">
            Cancel
          </button>
          <button
            type="button"
            class="comment__delete-confirm"
            :disabled="deleting"
            @click="deleteComment"
          >
            {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>

      <p v-if="actionError" class="comment__error" role="alert">{{ actionError }}</p>

      <div v-if="comment.children.length" class="comment__children">
        <comment-thread-item
          v-for="child in comment.children"
          :key="child.id"
          :comment="child"
          :depth="depth + 1"
          :page-id="pageId"
          @deleted="$emit('deleted', $event)"
          @reply="$emit('reply', $event)"
        />
      </div>
    </div>
  </article>
</template>

<script>
import {
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/firebase';
import {
  canDeleteComment,
  canEditComment,
} from '@/lib/comments/permissions';

export default {
  name: 'CommentThreadItem',
  props: {
    comment: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    pageId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      actionError: '',
      confirmingDelete: false,
      deleting: false,
      editDraft: this.comment.body || '',
      editing: false,
      saving: false,
    };
  },
  computed: {
    canDelete() {
      return canDeleteComment(this.currentUser, this.comment);
    },
    canEdit() {
      return canEditComment(this.currentUser, this.comment);
    },
    canSaveEdit() {
      const body = this.editDraft.trim();
      return Boolean(body && body !== this.comment.body);
    },
    commentLabel() {
      return this.depth > 0 ? 'reply' : 'comment';
    },
    initial() {
      return this.comment.authorName?.trim().charAt(0).toUpperCase() || 'R';
    },
    commentDate() {
      return this.comment.createdAt?.toDate?.() || null;
    },
    formattedDate() {
      if (!this.commentDate) return '';

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(this.commentDate);
    },
    isoDate() {
      return this.commentDate?.toISOString() || '';
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    editFieldId() {
      return `comment-edit-${this.comment.id}`;
    },
  },
  watch: {
    'comment.body': function syncEditDraft(body) {
      if (!this.editing) this.editDraft = body || '';
    },
    canDelete(allowed) {
      if (!allowed) this.confirmingDelete = false;
    },
    canEdit(allowed) {
      if (!allowed) this.cancelEdit();
    },
  },
  methods: {
    beginEdit() {
      if (!this.canEdit) return;

      this.actionError = '';
      this.confirmingDelete = false;
      this.editDraft = this.comment.body || '';
      this.editing = true;
      this.$nextTick(() => this.$refs.editField?.focus());
    },
    cancelEdit() {
      this.editDraft = this.comment.body || '';
      this.editing = false;
    },
    async saveEdit() {
      const body = this.editDraft.trim();
      if (!this.canEdit || !body || body === this.comment.body || this.saving) return;

      this.actionError = '';
      this.saving = true;
      try {
        await updateDoc(this.commentRef(), {
          body,
          updatedAt: serverTimestamp(),
        });
        this.editDraft = body;
        this.editing = false;
      } catch (error) {
        this.actionError = `That ${this.commentLabel} could not be updated. Please try again.`;
      } finally {
        this.saving = false;
      }
    },
    async deleteComment() {
      if (!this.canDelete || this.deleting) return;

      this.actionError = '';
      this.deleting = true;
      try {
        await deleteDoc(this.commentRef());
        this.$emit('deleted', this.comment);
      } catch (error) {
        this.actionError = `That ${this.commentLabel} could not be deleted. Please try again.`;
        this.deleting = false;
      }
    },
    commentRef() {
      return doc(db, 'pageComments', this.pageId, 'comments', this.comment.id);
    },
  },
};
</script>

<style scoped lang="scss">
.comment {
  display: grid;
  gap: 16px;
  grid-template-columns: 2px minmax(0, 1fr);
  margin-top: 28px;
}

.comment__rail {
  background: rgba(255, 255, 255, .12);
  border-radius: 2px;
}

.comment__header {
  align-items: center;
  display: flex;
  gap: 12px;
}

.comment__header div {
  display: flex;
  flex-direction: column;
}

.comment__header strong {
  color: #fff;
  font-size: .95rem;
}

.comment__meta {
  align-items: center;
  color: #8f8f8f;
  display: flex;
  font-size: .75rem;
  gap: 6px;
  margin-top: 2px;
}

.comment__meta span::before {
  content: '\00b7';
  margin-right: 6px;
}

.comment__meta span:first-child::before {
  content: none;
}

.comment__avatar {
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 50%;
  height: 38px;
  object-fit: cover;
  width: 38px;
}

.comment__avatar--fallback {
  align-items: center;
  background: #251111;
  color: #ef7777;
  display: flex;
  font-weight: 900;
  justify-content: center;
}

.comment p {
  color: #d4d4d4;
  line-height: 1.65;
  margin: 14px 0 8px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.comment__actions {
  display: flex;
  gap: 14px;
}

.comment__actions button,
.comment__edit-actions button,
.comment__delete-confirmation button {
  background: none;
  border: 0;
  color: #ef7777;
  cursor: pointer;
  font: inherit;
  font-size: .82rem;
  font-weight: 800;
  padding: 0;
}

.comment__actions button:hover,
.comment__actions button:focus-visible,
.comment__edit-actions button:hover,
.comment__edit-actions button:focus-visible,
.comment__delete-confirmation button:hover,
.comment__delete-confirmation button:focus-visible {
  color: #fff;
}

.comment__actions .comment__delete {
  color: #aaa;
}

.comment__edit {
  margin: 14px 0 8px;
}

.comment__edit textarea {
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 8px;
  color: #fff;
  font: inherit;
  line-height: 1.55;
  padding: 12px;
  resize: vertical;
  width: 100%;
}

.comment__edit textarea:focus {
  border-color: #e85a5a;
  outline: 2px solid rgba(232, 90, 90, .18);
}

.comment__edit-actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.comment__edit-actions small {
  color: #777;
}

.comment__edit-actions div,
.comment__delete-confirmation div {
  display: flex;
  gap: 12px;
}

.comment__edit-actions .comment__save,
.comment__delete-confirmation .comment__delete-confirm {
  color: #fff;
}

.comment__edit-actions button:disabled,
.comment__delete-confirmation button:disabled {
  cursor: not-allowed;
  opacity: .48;
}

.comment__delete-confirmation {
  align-items: center;
  background: rgba(232, 90, 90, .12);
  border: 1px solid rgba(232, 90, 90, .25);
  border-radius: 6px;
  color: #efaaaa;
  display: flex;
  font-size: .8rem;
  gap: 14px;
  justify-content: space-between;
  margin-top: 12px;
  padding: 10px;
}

.comment__error {
  color: #ff9d9d;
  font-size: .8rem;
  margin: 10px 0 0;
}

.comment__children {
  margin-left: 10px;
}

@media (max-width: 600px) {
  .comment__delete-confirmation {
    align-items: flex-start;
    flex-direction: column;
  }

  .comment__children {
    margin-left: 0;
  }
}
</style>
